import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { ServiceAdviceBlock } from "@/components/ServiceAdviceBlock";
import { ServiceCautionBlock } from "@/components/ServiceCautionBlock";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { TAX_FAQ_CATEGORIES } from "@/content/tax-corpus/faq-defs";

const ADVICE_TH = [
  "นับวันที่อยู่ในประเทศไทยของทุกปีภาษีไว้เป็นตาราง พร้อมเก็บสำเนาหน้าตราประทับเข้าออกและบัตร ตม. เพราะภาระการพิสูจน์อยู่ที่ผู้เสียภาษี",
  "แยกบัญชีเงินออมที่มีอยู่ก่อนวันที่ 1 มกราคม 2567 ออกจากบัญชีที่รับเงินได้ใหม่ และเก็บสเตทเมนต์ ณ วันที่ 31 ธันวาคม 2566 ไว้เป็นหลักฐานตั้งต้น",
  "อ่านอนุสัญญาภาษีซ้อนฉบับที่ใช้กับประเทศผู้จ่ายเงินได้ของท่านโดยตรง เพราะข้อบทเรื่องบำนาญ เงินปันผล และค่าสิทธิของแต่ละฉบับไม่เหมือนกัน",
  "ขอหนังสือรับรองการหักภาษี ณ ที่จ่ายตามมาตรา 50 ทวิ ทันทีที่ได้รับเงิน อย่ารอรวบรวมตอนสิ้นปี เพราะการตามเก็บย้อนหลังทำให้เสียสิทธิเครดิตภาษี",
  "วางปฏิทินภาษีรายเดือนและรายปีไว้ล่วงหน้า ทั้ง ภ.พ.30 ทุกวันที่ 15 เงินสมทบประกันสังคม ภ.ง.ด.51 ครึ่งรอบบัญชี และ ภ.ง.ด.50 ภายใน 150 วัน",
  "ตั้งรอบระยะเวลาบัญชีให้ตรงกับบริษัทแม่ตั้งแต่จดทะเบียน เพราะการเปลี่ยนรอบภายหลังต้องขออนุมัติจากอธิบดีกรมสรรพากร",
  "ขอเลขประจำตัวผู้เสียภาษี 13 หลักและลงทะเบียน e-Filing ให้เรียบร้อยก่อนถึงฤดูยื่นแบบ เพราะการยืนยันตัวตนครั้งแรกใช้เวลา",
  "ถ้าต้องใช้เอกสารภาษีไทยในต่างประเทศ ให้ถามข้อกำหนดของหน่วยงานปลายทางก่อนแปล ว่าต้องการคำรับรองของกรมการกงสุล ของสถานทูต หรือของผู้แปลที่ขึ้นทะเบียนในประเทศนั้น",
];

const CAUTIONS_TH = [
  "เข้าใจว่าประเภทวีซ่าเป็นตัวตัดสินภาระภาษี ทั้งที่กฎหมายดูจำนวนวันที่อยู่ในไทยและแหล่งของเงินได้",
  "คิดว่าทำงานทางไกลให้นายจ้างต่างประเทศแล้วไม่มีเงินได้ในไทย ทั้งที่หน้าที่งานที่ทำในไทยถือว่ามีแหล่งเงินได้ในไทยตามมาตรา 41",
  "โอนเงินก้อนใหญ่เข้าไทยโดยไม่มีเอกสารแสดงที่มาและช่วงเวลาที่เงินได้เกิดขึ้น ทำให้พิสูจน์ข้อยกเว้นตาม ป.162/2566 ไม่ได้",
  "อ้างว่าเสียภาษีในต่างประเทศแล้วจึงไม่ยื่นแบบในไทย ทั้งที่การขอเครดิตภาษีต้องยื่นแบบและแสดงใบเสร็จของต่างประเทศ",
  "ปล่อยบริษัทที่ยังไม่มีรายได้ไม่ปิดงบ ทำให้เกิดค่าปรับสะสมและติดขัดเมื่อจะต่อใบอนุญาตทำงานหรือเปิดบัญชีธนาคาร",
  "ประมาณการกำไรสุทธิใน ภ.ง.ด.51 ต่ำกว่าความจริงเกินร้อยละ 25 โดยไม่มีเหตุอันสมควร ซึ่งมีเงินเพิ่มตามประมวลรัษฎากร",
  "รายรับเกิน 1.8 ล้านบาทแล้วยังไม่จดทะเบียนภาษีมูลค่าเพิ่มภายใน 30 วัน ทำให้ต้องรับผิดภาษีขายย้อนหลังพร้อมเบี้ยปรับ",
  "ยื่นคำแปลงบการเงินที่ตัวเลขหรือหัวข้อไม่ตรงต้นฉบับทุกบรรทัด ซึ่งกองสัญชาติและนิติกรณ์และหน่วยงานปลายทางจะให้แก้ไขและเสียเวลารอบใหม่",
];

export const Route = createFileRoute("/tax/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/tax-corpus");
    return { faqs: m.TAX_FAQ_TH };
  },
  component: TaxFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `ภาษีและบัญชีสำหรับชาวต่างชาติในไทย — FAQ ${faqs.length} ข้อ`,
        description:
          "คำถามที่พบบ่อยเรื่องถิ่นที่อยู่ทางภาษี 180 วัน ภ.ง.ด.90/91 เงินได้จากต่างประเทศตาม ป.161/2566 ภาษีหัก ณ ที่จ่าย VAT งบการเงินนิติบุคคล อนุสัญญาภาษีซ้อน และการรับรองเอกสารภาษีเพื่อใช้ต่างประเทศ",
        path: "/tax/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/tax/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "FAQ ภาษีและบัญชีสำหรับชาวต่างชาติ", url: `${site}/tax/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/tax/faq")) },
      ],
    };
  },
});

function TaxFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>ภาษีและบัญชีสำหรับชาวต่างชาติ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          ภาษีและบัญชีสำหรับชาวต่างชาติและนิติบุคคลในประเทศไทย
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">
          รวมคำถาม {faqs.length} ข้อเรื่องถิ่นที่อยู่ทางภาษีตามประมวลรัษฎากร มาตรา 41 การยื่นแบบ ภ.ง.ด.90 และ ภ.ง.ด.91
          เงินได้จากแหล่งต่างประเทศตามคำสั่งกรมสรรพากรที่ ป.161/2566 และ ป.162/2566 ภาษีหัก ณ ที่จ่าย ภาษีมูลค่าเพิ่ม
          หน้าที่ทางบัญชีตาม พ.ร.บ.การบัญชี พ.ศ. 2543 อนุสัญญาภาษีซ้อน และการแปลรับรองเอกสารภาษีเพื่อใช้ในต่างประเทศ
        </p>

        <section aria-label="หมวดคำถาม" className="mb-10">
          <h2 className="mb-3 text-xl font-bold">เลือกตามหมวด</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {TAX_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/tax/faq/${c.slug}`} className="rounded-lg border bg-card p-4 hover:bg-muted">
                <span className="block font-semibold">{c.titleTh}</span>
                <span className="mt-1 block text-xs text-muted-foreground">{c.descTh}</span>
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <ServiceAdviceBlock items={ADVICE_TH} locale="th" />
        <ServiceCautionBlock items={CAUTIONS_TH} locale="th" />

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ให้เจ้าหน้าที่ช่วยตรวจสถานะภาษีของท่าน</h2>
          <p className="text-sm text-muted-foreground">
            แจ้งจำนวนวันที่อยู่ในไทยในปีภาษี ประเภทเงินได้ ประเทศต้นทางของเงิน และเอกสารที่ปลายทางต้องการ
            ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อเจ้าหน้าที่</a>
        </section>
        <div className="mt-8 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/tax/faq" className="text-primary underline">Read this page in English →</a>
        </div>
        <FaqSourceNote topic="tax" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}